import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { useFlag } from '@afterpaytouch/feature'
import { useRouter } from 'next/router'
import { RegistrationStatus, CheckoutStatus, Features, isOneOfferPageDecision } from '../model'
import { PageNames } from '../model/monitoring'
import {
  useAuthentication,
  useConsumerSelector,
  useHasSavedPaymentMethods,
  useRegistration,
  useHasSavedAddresses,
  useConsumerLendingEligible,
  useCheckoutState,
  useIsRecoverableFlow,
  useCheckoutError,
  useIsPayMonthlyOnly,
  usePaymentTypeAllowInConvergence,
  useIsShippingAddressValid,
  useAppRouter,
  useIsAppleRetailOrder,
  useConsumerFacilityCohort,
  useCashLoginType,
  useIsInlinePaymentOptions,
  useEffectiveRoutingDecision,
  useIsAUPayMonthlyOrderLimiteTerminal,
} from '../state'
import { PaymentType } from '@afterpay/types'
import { useIsCountry } from '../state'

export { Route, isRoute } from './routes'
import { Route } from './routes'

const APPLE_PAYLATER_VERIFY_PATH = '/apple-paylater/verify'

export const VERIFY_PREFETCH_ROUTE_COUNTRY_MAP = {
  [Country.US]: [],
  [Country.AU]: [Route.VERIFY_LICENCE, Route.VERIFY_MEDICARE, Route.VERIFY_PASSPORT],
  [Country.NZ]: [Route.VERIFY_LICENCE, Route.VERIFY_MEDICARE, Route.VERIFY_PASSPORT],
  [Country.GB]: [Route.VERIFY_BIOMETRIC_LICENCE, Route.VERIFY_BIOMETRIC_PASSPORT],
  [Country.CA]: [Route.VERIFY_IDENTITY, Route.VERIFY_BIOMETRIC_PERSONA],
}

export const getPrefetchRoutes = (country: Country): Route[] => {
  const VerifyRoutes = VERIFY_PREFETCH_ROUTE_COUNTRY_MAP[country]
  if (VerifyRoutes === undefined) {
    return []
  }
  return VerifyRoutes
}

export const editShippingAddressRoute = (shippingAddressId: number | string | undefined): Route =>
  `${Route.EDIT_SHIPPING_ADDRESS}/${shippingAddressId}` as unknown as Route

const applePaylaterRoutePageNames = {
  [Route.APPLE_PAYLATER_ADD_PAYMENT_METHOD]: PageNames.ADD_PAYMENT_METHOD,
  [Route.APPLE_PAYLATER_CARD_SCAN]: PageNames.CARD_SCAN,
  [Route.APPLE_PAYLATER_CASH_AUTH]: PageNames.CASH_AUTH,
  [Route.APPLE_PAYLATER_CONTRACT_AGREEMENT]: PageNames.CONTRACT_AGREEMENT,
  [Route.APPLE_PAYLATER_CREATE_PASSWORD]: PageNames.CREATE_PASSWORD,
  [Route.APPLE_PAYLATER_EDIT_SHIPPING_ADDRESS_WITH_ID]: PageNames.EDIT_SHIPPING_ADDRESS,
  [Route.APPLE_PAYLATER_KYC_CONFIRM_DETAILS]: PageNames.KYC_CONFIRM_DETAILS,
  [Route.APPLE_PAYLATER_KYC_UPDATE_ADDRESS]: PageNames.KYC_UPDATE_ADDRESS,
  [Route.APPLE_PAYLATER_LOGGED_IN]: PageNames.APPLE_PAYLATER_LOGGED_IN,
  [Route.APPLE_PAYLATER_LOGIN]: PageNames.APPLE_PAYLATER_LOGIN_EMAIL,
  [Route.APPLE_PAYLATER_LOGIN_ONE_TIME_CODE]: PageNames.ONE_TIME_PASSWORD,
  [Route.APPLE_PAYLATER_NEW_PAYMENT_METHOD]: PageNames.ADD_NEW_PAYMENT_METHOD,
  [Route.APPLE_PAYLATER_NEW_SHIPPING_ADDRESS]: PageNames.ADD_NEW_SHIPPING_ADDRESS,
  [Route.APPLE_PAYLATER_OH_NO]: PageNames.TERMINAL_ERROR,
  [Route.APPLE_PAYLATER_PASSWORD]: PageNames.APPLE_PAYLATER_LOGIN_PASSWORD,
  [Route.APPLE_PAYLATER_PAYMENT_METHOD]: PageNames.PAYMENT_METHOD,
  [Route.APPLE_PAYLATER_PAYMENT_OPTIONS]: PageNames.PAYMENT_OPTIONS,
  [Route.APPLE_PAYLATER_PAYMENT_TYPES]: PageNames.PAYMENT_TYPES,
  [Route.APPLE_PAYLATER_PREFERRED_PAYMENT_DAY]: PageNames.PREFERRED_PAYMENT_DAY,
  [Route.APPLE_PAYLATER_RETRY_VERIFY_LICENCE]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.APPLE_PAYLATER_RETRY_VERIFY_MEDICARE]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.APPLE_PAYLATER_RETRY_VERIFY_PASSPORT]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.APPLE_PAYLATER_SHIPPING_ADDRESS]: PageNames.SHIPPING_ADDRESS,
  [Route.APPLE_PAYLATER_SIGN_UP]: PageNames.SIGN_UP,
  [Route.APPLE_PAYLATER_SUMMARY]: PageNames.APPLE_PAYLATER_SUMMARY,
  [Route.APPLE_PAYLATER_UNSUITABILITY_QUESTION_FORM]: PageNames.UNSUITABILITY_QUESTIONS,
  [Route.APPLE_PAYLATER_VERIFY_ADDRESS]: PageNames.SIGNUP_RETRY_ADDRESS,
  [Route.APPLE_PAYLATER_VERIFY_BIOMETRIC_PERSONA]: PageNames.VERIFY_BIOMETRIC_PERSONA,
  [Route.APPLE_PAYLATER_VERIFY_CHALLENGE]: PageNames.VERIFY_CHALLENGE,
  [Route.APPLE_PAYLATER_VERIFY_IDENTITY]: PageNames.VERIFY_IDENTITY,
  [Route.APPLE_PAYLATER_VERIFY_IDENTITY_START]: PageNames.VERIFY_IDENTITY_START,
  [Route.APPLE_PAYLATER_VERIFY_LICENCE]: PageNames.VERIFY_LICENCE,
  [Route.APPLE_PAYLATER_VERIFY_MEDICARE]: PageNames.VERIFY_MEDICARE,
  [Route.APPLE_PAYLATER_VERIFY_PASSPORT]: PageNames.VERIFY_PASSPORT,
  [Route.APPLE_PAYLATER_VERIFY_RETRY]: PageNames.SIGNUP_RETRY,
  [Route.APPLE_PAYLATER_VERIFYING_BIOMETRIC]: PageNames.VERIFYING_BIOMETRIC,
} satisfies Partial<Record<Route, PageNames>>

export const routePageNames: Record<Route, PageNames> = {
  [Route.APPLE_PAYLATER_ADD_CARD]: PageNames.APPLE_PAYLATER_ADD_CARD,
  [Route.ADD_MOBILE]: PageNames.ADD_MOBILE,
  [Route.ADD_PAYMENT_METHOD]: PageNames.ADD_PAYMENT_METHOD,
  [Route.ADJUST_AMOUNT]: PageNames.ADJUST_AMOUNT,
  [Route.APPLE_PAYLATER_INITIALISE]: PageNames.APPLE_PAYLATER_INITIALISE,
  [Route.APPLE_PAYLATER_INITIAL_LOADING]: PageNames.APPLE_PAYLATER_INITIAL_LOADING,
  [Route.CARD_EDUCATION]: PageNames.CARD_EDUCATION,
  ...applePaylaterRoutePageNames,
  [Route.CASH_AUTH]: PageNames.CASH_AUTH,
  [Route.CASH_AUTH_INLINE]: PageNames.CASH_AUTH_INLINE,
  [Route.CASH_APP_PAY_REMEDIATION]: PageNames.CASH_APP_PAY_REMEDIATION,
  [Route.CASH_APP_PAY_UNAVAILABLE]: PageNames.CASH_APP_PAY_UNAVAILABLE,
  [Route.CREATE_PASSWORD]: PageNames.CREATE_PASSWORD,
  [Route.ENTER_EMAIL]: PageNames.LOGIN_EMAIL,
  [Route.ENTER_PASSWORD]: PageNames.LOGIN_PASSWORD,
  [Route.PASSWORD_RESET_REQUIRED]: PageNames.PASSWORD_RESET_REQUIRED,
  [Route.NEW_PAYMENT_METHOD]: PageNames.ADD_NEW_PAYMENT_METHOD,
  [Route.NEW_SHIPPING_ADDRESS]: PageNames.ADD_NEW_SHIPPING_ADDRESS,
  [Route.EDIT_SHIPPING_ADDRESS]: PageNames.EDIT_SHIPPING_ADDRESS,
  [Route.EDIT_SHIPPING_ADDRESS_WITH_ID]: PageNames.EDIT_SHIPPING_ADDRESS,
  [Route.ONE_TIME_PASSWORD]: PageNames.ONE_TIME_PASSWORD,
  [Route.PAY]: PageNames.PAY,
  [Route.PAYMENT_METHOD]: PageNames.PAYMENT_METHOD,
  [Route.PAYMENT_OPTIONS]: PageNames.PAYMENT_OPTIONS,
  [Route.PAYMENT_TYPES]: PageNames.PAYMENT_TYPES,
  [Route.PREFERRED_PAYMENT_DAY]: PageNames.PREFERRED_PAYMENT_DAY,
  [Route.LOGGED_IN]: PageNames.LOGGED_IN,
  [Route.SHIPPING_ADDRESS]: PageNames.SHIPPING_ADDRESS,
  [Route.SIGN_UP]: PageNames.SIGN_UP,
  [Route.SUMMARY]: PageNames.SUMMARY,
  [Route.TERMINAL_ERROR]: PageNames.TERMINAL_ERROR,
  [Route.VERIFY]: PageNames.VERIFY,
  [Route.VERIFY_CHALLENGE]: PageNames.VERIFY_CHALLENGE,
  [Route.VERIFY_IDENTITY]: PageNames.VERIFY_IDENTITY,
  [Route.VERIFYING_BIOMETRIC]: PageNames.VERIFYING_BIOMETRIC,
  [Route.VERIFY_LICENCE]: PageNames.VERIFY_LICENCE,
  [Route.VERIFY_MEDICARE]: PageNames.VERIFY_MEDICARE,
  [Route.VERIFY_PASSPORT]: PageNames.VERIFY_PASSPORT,
  [Route.VERIFY_BIOMETRIC_LICENCE]: PageNames.VERIFY_LICENCE,
  [Route.VERIFY_BIOMETRIC_PASSPORT]: PageNames.VERIFY_PASSPORT,
  [Route.VERIFY_IDENTITY_START]: PageNames.VERIFY_IDENTITY_START,
  [Route.RETRY_VERIFY_LICENCE]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.RETRY_VERIFY_PASSPORT]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.RETRY_VERIFY_MEDICARE]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.VERIFY_ADDRESS]: PageNames.SIGNUP_RETRY_ADDRESS,
  [Route.VERIFY_RETRY]: PageNames.SIGNUP_RETRY,
  [Route.CARD_SCAN]: PageNames.CARD_SCAN,
  [Route.AMOUNT_TOO_HIGH]: PageNames.LIMIT_EXCEEDED,
  [Route.ORDER_LIMIT_EXCEEDED]: PageNames.LIMIT_EXCEEDED,
  [Route.PAY_MONTHLY]: PageNames.PAY_MONTHLY,
  [Route.PAY_MONTHLY_ONLY]: PageNames.PAY_MONTHLY_ONLY,
  [Route.INDEX]: PageNames.INDEX,
  [Route.INITIAL_LOADING]: PageNames.INITIAL_LOADING,
  [Route.RETRY_CREDIT_FREEZE]: PageNames.RETRY_CREDIT_FREEZE,
  [Route.OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE]: PageNames.OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE,
  [Route.KYC_CONFIRM_DETAILS]: PageNames.KYC_CONFIRM_DETAILS,
  [Route.KYC_UPDATE_ADDRESS]: PageNames.KYC_UPDATE_ADDRESS,
  [Route.UNSUITABILITY_QUESTION_FORM]: PageNames.UNSUITABILITY_QUESTIONS,
  [Route.AFFORDABILITY_QUESTION_FORM]: PageNames.AFFORDABILITY_QUESTIONS,
  [Route.CONTRACT_AGREEMENT]: PageNames.CONTRACT_AGREEMENT,
  [Route.VERIFY_BIOMETRIC_PERSONA]: PageNames.VERIFY_BIOMETRIC_PERSONA,
  [Route.EDUCATION_INTRO]: PageNames.EDUCATION_INTRO,
  [Route.GET_STARTED]: PageNames.GET_STARTED,
  [Route.PURCHASE_SUCCESS]: PageNames.PURCHASE_SUCCESS,
  [Route.PROFILE_CREATED]: PageNames.PROFILE_CREATED,
  [Route.WELCOME_WITH_INITIAL_LIMIT]: PageNames.WELCOME_WITH_INITIAL_LIMIT,
}

export const useRoutePageName = (): PageNames => {
  const { asPath, pathname, query } = useRouter()
  const auth = useAuthentication()
  const checkoutError = useCheckoutError()
  const registration = useRegistration()
  const { verifyAccountMobileRequired = false, hasPassword = false } = useConsumerSelector()
  const isRegistrationFlow = registration.status >= RegistrationStatus.Created

  if (pathname === Route.VERIFY || pathname === APPLE_PAYLATER_VERIFY_PATH) {
    if (isRegistrationFlow) {
      return PageNames.REGISTER_VERIFY
    } else if (verifyAccountMobileRequired) {
      return PageNames.ADD_MOBILE_VERIFY
    } else if (auth.verificationRequired) {
      return PageNames.NEW_DEVICE_VERIFY
    } else if (checkoutError?.errorCode === ErrorCode.CheckoutConfirm2FARequired || checkoutError?.errorCode === ErrorCode.CheckoutConfirm2FARequiredNoEmail) {
      return PageNames.CHECKOUT_CONFIRM_VERIFY
    } else if (checkoutError?.errorCode === ErrorCode.ExtendedFraudAlert) {
      return PageNames.EFA_VERIFY
    } else if (!hasPassword) {
      return PageNames.FIRST_RETURN_VERIFY
    } else {
      return PageNames.RESET_VERIFY
    }
  } else if (query?.document !== undefined || query?.error !== undefined) {
    return routePageNames[asPath]
  }
  return routePageNames[pathname] ?? PageNames.NOT_FOUND
}

export const useSummaryRoute = (): Route => {
  const isRecoverableFlow = useIsRecoverableFlow()
  const recoverableRoute = useRecoverableTerminalErrorRoute()

  if (isRecoverableFlow) {
    return recoverableRoute
  }

  return Route.SUMMARY
}

export const errorCodeRoute: Partial<Record<ErrorCode, Route>> = {
  [ErrorCode.AmountTooHigh]: Route.AMOUNT_TOO_HIGH,
  [ErrorCode.MaxActiveOrdersReached]: Route.ORDER_LIMIT_EXCEEDED,
  [ErrorCode.OverduePaymentsCheckoutPaymentEligible]: Route.OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE,
}

export const useRecoverableTerminalErrorRoute = (): Route => {
  const checkoutState = useCheckoutState()
  const errorCode = checkoutState.error?.errorCode ?? ''

  return errorCodeRoute[errorCode]
}

export const useRouteBackLink = (): Route => {
  const { asPath, pathname } = useRouter()
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()
  const hasSavedAddresses = useHasSavedAddresses()
  const summaryRoute = useSummaryRoute()
  const recoverableRoute = useRecoverableTerminalErrorRoute()
  const isRecoverableFlow = useIsRecoverableFlow()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  if (pathname === Route.EDIT_SHIPPING_ADDRESS_WITH_ID) {
    return isArcadeDesignLanguage ? summaryRoute : Route.SHIPPING_ADDRESS
  }

  switch (asPath) {
    case Route.NEW_PAYMENT_METHOD:
      if (isArcadeDesignLanguage) {
        return summaryRoute
      }
      if (hasSavedPaymentMethods) {
        return Route.PAYMENT_METHOD
      }
      if (isRecoverableFlow) {
        return recoverableRoute
      }
      return summaryRoute
    case Route.CARD_SCAN:
    case Route.PAYMENT_METHOD:
      return isRecoverableFlow ? recoverableRoute : summaryRoute
    case Route.SHIPPING_ADDRESS:
    case Route.PREFERRED_PAYMENT_DAY:
      return summaryRoute
    case Route.NEW_SHIPPING_ADDRESS:
      return hasSavedAddresses && !isArcadeDesignLanguage ? Route.SHIPPING_ADDRESS : summaryRoute
    case Route.VERIFY_LICENCE:
    case Route.VERIFY_MEDICARE:
    case Route.VERIFY_PASSPORT:
      return Route.VERIFY_IDENTITY
    default:
      return asPath as Route
  }
}

export const usePostLoginRoute = (): Route => {
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const { status } = useCheckoutState()
  const isRecoverableFlow = useIsRecoverableFlow()
  const recoverableRoute = useRecoverableTerminalErrorRoute()
  const isPayMonthlyOnly = useIsPayMonthlyOnly()
  const isConsumerLendingAllowInConvergence = usePaymentTypeAllowInConvergence(PaymentType.PCL)
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const consumerFacilityCohort = useConsumerFacilityCohort()
  const isInlinePaymentOptions = useIsInlinePaymentOptions()
  const routingDecision = useEffectiveRoutingDecision()
  const isPayMonthlyOrderLimitTerminal = useIsAUPayMonthlyOrderLimiteTerminal()

  if (isRecoverableFlow) {
    return recoverableRoute
  }

  if (isPayMonthlyOrderLimitTerminal) {
    return Route.TERMINAL_ERROR
  }

  if (status < CheckoutStatus.Starting) {
    return Route.LOGGED_IN
  }

  if (consumerFacilityCohort?.eligibility === 'eligible' && isAppleRetailOrder) {
    return Route.GET_STARTED
  }

  if (!isConsumerLendingAllowInConvergence) {
    return Route.SUMMARY
  }

  if (isAppleRetailOrder) {
    return Route.SUMMARY
  }

  if (routingDecision === 'PBI_ONLY') {
    return Route.SUMMARY
  }
  if (isOneOfferPageDecision(routingDecision)) {
    return Route.PAYMENT_OPTIONS
  }

  if (isConsumerLendingEligible && isPayMonthlyOnly) {
    return Route.PAY_MONTHLY_ONLY
  }

  if (isConsumerLendingEligible) {
    return isInlinePaymentOptions ? Route.PAYMENT_OPTIONS : Route.PAYMENT_TYPES
  }

  return Route.SUMMARY
}

export const useNavigateToShippingAddress = (): (() => void) => {
  const router = useAppRouter()
  const hasSavedAddresses = useHasSavedAddresses()
  const hasValidShippingAddress = useIsShippingAddressValid()

  const routeToNavigateTo = !hasValidShippingAddress && !hasSavedAddresses ? Route.NEW_SHIPPING_ADDRESS : Route.SHIPPING_ADDRESS
  return () => router.push(routeToNavigateTo)
}

export const CONSUMER_LENDING_ROUTES = [Route.PAYMENT_OPTIONS, Route.PAYMENT_TYPES]

export const ERROR_TO_ONBOARDING_ROUTE = {
  [ErrorCode.UnsuitabilityNotFound]: Route.UNSUITABILITY_QUESTION_FORM,
  [ErrorCode.UnsuitabilityExpired]: Route.UNSUITABILITY_QUESTION_FORM,
  [ErrorCode.ContractAcceptanceRequired]: Route.CONTRACT_AGREEMENT,
} as const

export const usePostGetStartedRoute = (): Route | undefined => {
  const consumerFacilityCohort = useConsumerFacilityCohort()
  const checkoutError = useCheckoutError()

  const errorCode = checkoutError?.errorCode

  if (consumerFacilityCohort?.getStartedContext === 'LCCC_EXISTING') {
    return undefined
  }

  if (errorCode === undefined) {
    return Route.UNSUITABILITY_QUESTION_FORM
  }

  return ERROR_TO_ONBOARDING_ROUTE[errorCode] ?? Route.UNSUITABILITY_QUESTION_FORM
}

export const useCashAuthRoute = (): Route => {
  const loginType = useCashLoginType()
  const isCashLoginDomainApisEnabled = useFlag(Features.ENABLE_CASH_LOGIN_DOMAIN_APIS, false)

  if (loginType === 'inline' && isCashLoginDomainApisEnabled) {
    return Route.CASH_AUTH_INLINE
  }

  return Route.CASH_AUTH
}

export const usePostRegistrationRoute = (): Route => {
  const postLoginRoute = usePostLoginRoute()
  const isGbCountry = useIsCountry(Country.GB)

  return isGbCountry ? Route.PROFILE_CREATED : postLoginRoute
}
